import Card from "components/DesignSystem/Card/Card";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";

import Table from "components/DesignSystem/Table/V2/Table";
import classNames from "classnames";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  CsAgent,
  CsChatSummary,
  CsSortOrder,
  useGetCsChatSectionQuery,
} from "store/apis/supportDashboard";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { CsStatCard } from "./CsStatCard";
import { TableSkeletonRows } from "./CsOverviewCells";
import { PERFORMANCE_SORT_OPTIONS } from "./utils/csOverview";

const columnHelper = createColumnHelper<CsAgent>();

const columns = [
  columnHelper.accessor("name", {
    header: "Agent",
    size: 22,
    cell: (info) => (
      <span className="t-text-body-sm t-font-medium t-text-text-100">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("chats", {
    header: "Total chats",
    size: 12,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("responseTimeStr", {
    header: "Avg response time",
    size: 18,
    cell: (info) => (
      <span className="t-text-body-sm t-font-semibold t-text-text-100">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("chatsPerCustomer", {
    header: "Chats / customer",
    size: 16,
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">
        {info.getValue().toFixed(2)}
      </span>
    ),
  }),
  columnHelper.accessor("repliedUnder1hr", {
    header: "Replied < 1hr",
    size: 14,
    cell: (info) => {
      const row = info.row.original;
      const hasReplied = row.repliedUnder1hr > 0;
      return (
        <span className="t-text-body-sm t-text-text-60">
          <span
            className={classNames("t-font-semibold", {
              "t-text-dark_green-70": hasReplied,
              "t-text-neutral-40": !hasReplied,
            })}
          >
            {row.repliedUnder1hr}
          </span>
          {row.chats > 0 && (
            <span className="t-text-text-30 t-ml-1">({row.under1hrPct}%)</span>
          )}
        </span>
      );
    },
  }),
];

type ChatStatCardsProps = {
  summary: CsChatSummary;
  agentCount: number;
};

const ChatStatCards = ({ summary, agentCount }: ChatStatCardsProps) => (
  <div className="t-grid t-grid-cols-3 t-gap-3 t-mb-3">
    <CsStatCard
      label="Total messages"
      value={summary.totalChats}
      sub={`Across ${agentCount} agents`}
    />
    <CsStatCard
      label="Avg response time"
      value={<span>{summary.avgResponseTimeStr}</span>}
      sub="Across all agents in chat"
    />
    <CsStatCard
      label="Replied < 1 hour"
      value={summary.totalRepliedUnder1hr}
      sub={`${summary.repliedUnder1hrPct}% of ${summary.totalChats} chats`}
    />
  </div>
);

type CsChatSectionProps = {
  summary: CsChatSummary;
  agentCount: number;
  dateFrom: string;
  dateTo: string;
};

export const CsChatSection = ({
  summary,
  agentCount,
  dateFrom,
  dateTo,
}: CsChatSectionProps) => {
  const query = useQuery();
  const { update: updateQuery } = useUpdateQuery();

  const page = Number(query.get("chat_page") || "1");
  const sortOrder = (query.get("chat_sort") as CsSortOrder) || "DSC";

  const handleSortChange = (option: { value: string } | null) => {
    if (option) updateQuery({ query: "chat_sort", value: option.value });
  };

  const responseTimeSortOrder: CsSortOrder =
    sortOrder === "DSC" ? "ASC" : "DSC";

  const { data, isFetching } = useGetCsChatSectionQuery({
    start_date: dateFrom,
    end_date: dateTo,
    sort_key: "RESPONSE_TIME",
    sort_order: responseTimeSortOrder,
    page_num: page,
  });

  const agents: CsAgent[] = data?.agents ?? [];
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? 1;

  const table = useReactTable({
    data: agents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: 10, minSize: 1, maxSize: 100 },
  });

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <ChatStatCards summary={summary} agentCount={agentCount} />

      <Card.Root borderRounded="lg" className="t-overflow-hidden">
        <Card.Header>
          <Card.Title>Agent Performance</Card.Title>
        </Card.Header>

        <Card.Body>
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-flex t-items-center t-gap-1.5">
              <span className="t-text-body-sm t-text-text-30">Sort:</span>
              <Combobox
                size="small"
                isClearable={false}
                options={PERFORMANCE_SORT_OPTIONS}
                value={PERFORMANCE_SORT_OPTIONS.find(
                  (o) => o.value === sortOrder
                )}
                onChange={(o) =>
                  handleSortChange(o as { value: string } | null)
                }
              />
            </div>
            <PaginationHandler
              page_num={page}
              per_page={5}
              total_count={totalCount}
              total_pages={totalPages}
              onPageNumChange={(p) =>
                updateQuery({ query: "chat_page", value: String(p) })
              }
              isLoading={isFetching}
            />
          </div>
        </Card.Body>

        <Table.Container
          isLoading={isFetching}
          sticky={false}
          className="t-pt-4"
        >
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((hg) => (
                <Table.HeadRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <Table.HeadCell
                      key={h.id}
                      style={{ width: `${h.getSize()}%` }}
                      isUpperCase={false}
                    >
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </Table.HeadCell>
                  ))}
                </Table.HeadRow>
              ))}
            </Table.Head>
            <Table.Body>
              {isFetching ? (
                <TableSkeletonRows columns={columns} />
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </Card.Root>
    </div>
  );
};
